import React from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Link, CircleAlert } from 'lucide-react'
import { RichTextInput, MentionItem } from 'components/Chat/components/RichTextInput/RichTextInput'
import colors from 'colors'

interface IMeetingDetailsFormProps {
  // Location
  location: string
  onLocationChange: (location: string) => void

  // Video Conferencing Link
  meetingLink: string
  onMeetingLinkChange: (link: string) => void
  linkCopied: boolean
  onCopyLink: () => void
  linkError: string

  // Description
  description: string
  onDescriptionChange: (description: string) => void

  // Mention support for description
  mentionProvider?: (query: string) => Promise<MentionItem[]> | MentionItem[]
  initialMentions?: MentionItem[]
}

export const MeetingDetailsForm: React.FC<IMeetingDetailsFormProps> = ({
  location,
  onLocationChange,
  meetingLink,
  onMeetingLinkChange,
  linkCopied,
  onCopyLink,
  linkError,
  description,
  onDescriptionChange,
  mentionProvider,
  initialMentions,
}) => {
  const { t } = useTranslation()
  return (
    <>
      {/* Location */}
      <div className="mb-5">
        <label className="block text-sm font-semibold text-primary-text mb-2">
          {t('calendar.modal.location')}
        </label>
        <div className="flex items-center border border-primary-outline rounded-lg px-3 py-2 shadow">
          <MapPin className="w-4 h-4 mr-2 opacity-70" />
          <input
            type="text"
            value={location}
            onChange={(e) => onLocationChange(e.target.value)}
            placeholder={t('calendar.modal.addLocation')}
            className="flex-1 text-sm outline-none bg-transparent"
          />
        </div>
      </div>

      {/* Video Conferencing Link */}
      <div className="mb-5">
        <label className="block text-sm font-semibold text-primary-text mb-2">
          {t('calendar.modal.link')}
        </label>
        <div
          className={`flex items-center border rounded-lg px-3 py-2 shadow ${
            linkError ? 'border-alert' : 'border-primary-outline'
          }`}
          style={linkError ? { borderColor: colors.alert.DEFAULT } : {}}
        >
          <Link className="w-4 h-4 mr-2 opacity-70" />
          <input
            type="text"
            value={meetingLink}
            onChange={(e) => onMeetingLinkChange(e.target.value)}
            placeholder={t('calendar.modal.addVideoConferencing')}
            className="flex-1 bg-transparent text-sm text-primary-text outline-none"
          />
          <button
            onClick={onCopyLink}
            disabled={!meetingLink.trim()}
            className={`text-sm font-medium transition-colors pl-2 ${
              linkCopied
                ? 'text-green-600'
                : meetingLink.trim()
                ? 'text-primary hover:underline'
                : 'text-silver-light cursor-not-allowed'
            }`}
          >
            {linkCopied ? t('calendar.modal.copied') : t('calendar.modal.copy')}
          </button>
        </div>
        {linkError && (
          <p
            className="text-xs mt-1.5 flex items-center gap-1 font-medium"
            style={{ color: colors.alert.DEFAULT }}
          >
            <CircleAlert className="w-3.5 h-3.5 flex-shrink-0" color={colors.alert.DEFAULT} />
            {linkError}
          </p>
        )}
      </div>

      {/* Description */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-primary-text mb-2">
          {t('calendar.modal.descriptionAndDetails')}
        </label>
        <RichTextInput
          placeholder={t('calendar.modal.addDescription')}
          value={description}
          onChange={onDescriptionChange}
          mode="form"
          // Attach button intentionally hidden — file attachments tracked in #6132
          onAttachClick={undefined}
          mentionProvider={mentionProvider}
          initialMentions={initialMentions}
        />
      </div>
    </>
  )
}
